import { Input } from '@shared/components/ui';
import { Archive, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RetentionProps {
  data: {
    retention_policy?: {
      keep_daily?: number;
      keep_weekly?: number;
      keep_monthly?: number;
      keep_last?: number;
    };
  };
  onChange: (data: unknown) => void;
  onNext: () => void;
  onBack: () => void;
}

export function Retention({ data, onChange, onNext: _onNext, onBack: _onBack }: RetentionProps) {
  const { t } = useTranslation('backups');
  const [errors, setErrors] = useState<{
    keep_daily?: string;
    keep_weekly?: string;
    keep_monthly?: string;
    keep_last?: string;
  }>({});

  const policy = data.retention_policy || {
    keep_daily: 7,
    keep_weekly: 4,
    keep_monthly: 6,
    keep_last: 0,
  };

  const updatePolicy = (key: string, value: number | undefined) => {
    onChange({
      ...data,
      retention_policy: {
        ...policy,
        [key]: value === undefined || isNaN(value) ? 0 : value,
      },
    });
    setErrors({ ...errors, [key]: undefined });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-2xl flex items-start space-x-3">
        <Info className="text-text-muted-light dark:text-text-muted-dark shrink-0" size={20} />
        <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark leading-relaxed">
          {t('wizard.retention.info')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <Archive className="text-brand-600 dark:text-brand-400" size={18} />
            <label
              htmlFor="backup-retention-last"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.retention.keepLastN')}
            </label>
          </div>
          <Input
            id="backup-retention-last"
            aria-describedby="backup-retention-last-help"
            type="number"
            min={0}
            max={100}
            placeholder={t('wizard.retention.placeholderDisabled')}
            value={policy.keep_last || ''}
            onChange={(e) => updatePolicy('keep_last', parseInt(e.target.value))}
            error={errors.keep_last}
            className="text-center"
          />
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            <span id="backup-retention-last-help">{t('wizard.retention.keepLastNHelp')}</span>
          </p>
        </div>

        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <Archive className="text-state-info-light dark:text-state-info-dark" size={18} />
            <label
              htmlFor="backup-retention-daily"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.retention.daily')}
            </label>
          </div>
          <Input
            id="backup-retention-daily"
            type="number"
            min={0}
            max={365}
            placeholder="7"
            value={policy.keep_daily || ''}
            onChange={(e) => updatePolicy('keep_daily', parseInt(e.target.value))}
            error={errors.keep_daily}
            className="text-center"
          />
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.retention.dailyHelp')}
          </p>
        </div>

        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <Archive className="text-state-ok-light dark:text-state-ok-dark" size={18} />
            <label
              htmlFor="backup-retention-weekly"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.retention.weekly')}
            </label>
          </div>
          <Input
            id="backup-retention-weekly"
            type="number"
            min={0}
            max={52}
            placeholder="4"
            value={policy.keep_weekly || ''}
            onChange={(e) => updatePolicy('keep_weekly', parseInt(e.target.value))}
            error={errors.keep_weekly}
            className="text-center"
          />
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.retention.weeklyHelp')}
          </p>
        </div>

        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <Archive className="text-state-warn-light dark:text-state-warn-dark" size={18} />
            <label
              htmlFor="backup-retention-monthly"
              className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
            >
              {t('wizard.retention.monthly')}
            </label>
          </div>
          <Input
            id="backup-retention-monthly"
            type="number"
            min={0}
            max={120}
            placeholder="6"
            value={policy.keep_monthly || ''}
            onChange={(e) => updatePolicy('keep_monthly', parseInt(e.target.value))}
            error={errors.keep_monthly}
            className="text-center"
          />
          <p className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.retention.monthlyHelp')}
          </p>
        </div>
      </div>

      <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl">
        <p className="text-xs font-medium text-state-info-light dark:text-state-info-dark">
          <strong>{t('wizard.retention.example')}</strong> {t('wizard.retention.exampleText')}
        </p>
      </div>
    </div>
  );
}
